import React from 'react';
import { ProjectFilters as ProjectFiltersType } from '@models/project.model';
import { ProjectStatus, getProjectStatusLabel } from '@models/projectStatus.model';
import { Input } from '@components/atoms/Input';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';

interface ProjectFiltersProps {
  filters: ProjectFiltersType;
  onFiltersChange: (filters: ProjectFiltersType) => void;
  onClear: () => void;
}

const ProjectFilters: React.FC<ProjectFiltersProps> = ({
  filters,
  onFiltersChange,
  onClear,
}) => {
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({ ...filters, search: e.target.value });
  };

  const handleStatusChange = (value: any) => {
    onFiltersChange({
      ...filters,
      states: value?.value ? [value.value] : undefined,
    });
  };

  const handleSortByChange = (value: any) => {
    onFiltersChange({
      ...filters,
      sortBy: value?.value as ProjectFiltersType['sortBy']
    });
  };

  // Create options for the status select
  const statusOptions: SelectOption[] = [
    { value: '', label: 'All Statuses' },
    { value: ProjectStatus.ONGOING, label: getProjectStatusLabel(ProjectStatus.ONGOING) },
    { value: ProjectStatus.BEHIND, label: getProjectStatusLabel(ProjectStatus.BEHIND) },
    { value: ProjectStatus.COMPLETED, label: getProjectStatusLabel(ProjectStatus.COMPLETED) },
    { value: ProjectStatus.ABANDONED, label: getProjectStatusLabel(ProjectStatus.ABANDONED) },
    { value: ProjectStatus.DELAYED, label: getProjectStatusLabel(ProjectStatus.DELAYED) },
    { value: ProjectStatus.BIDDING, label: getProjectStatusLabel(ProjectStatus.BIDDING) },
    { value: ProjectStatus.DRAFT, label: getProjectStatusLabel(ProjectStatus.DRAFT) },
    { value: ProjectStatus.BEHIND, label: getProjectStatusLabel(ProjectStatus.BEHIND) },
  ];

  // Create options for the sort by select
  const sortByOptions: SelectOption[] = [
    { value: 'name', label: 'Name' },
    { value: 'startDate', label: 'Start Date' },
    { value: 'endDate', label: 'End Date' },
  ];

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Filter Projects</h3>
        <button type="button"
          onClick={onClear}
          className="text-sm text-gray-500 hover:text-gray-700 underline"
        >
          Clear Filters
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Search Input */}
        <div>
          <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
            Search by name
          </label>
          <Input
            id="search"
            type="text"
            placeholder="Enter project name..."
            value={filters.search || ''}
            onChange={handleSearchChange}
          />
        </div>

        {/* Status Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Status
          </label>
          <ReactSelect
            options={statusOptions}
            value={statusOptions.find(option => option.value === filters.states?.[0]) || statusOptions[0]}
            onChange={handleStatusChange}
            placeholder="Select status..."
            isClearable={false}
            isSearchable={false}
          />
        </div>

        {/* Sort By */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Sort by
          </label>
          <ReactSelect
            options={sortByOptions}
            value={sortByOptions.find(option => option.value === filters.sortBy) || sortByOptions[0]}
            onChange={handleSortByChange}
            placeholder="Select sort field..."
            isClearable={false}
            isSearchable={false}
          />
        </div>
      </div>
    </div>
  );
};

export default ProjectFilters;